import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWSMessage } from '../context/WSMessageContext';

export default function useWSFeedbackMessages(debugMode = false) {
  const { messages } = useWSMessage();
  const [activeMessage, setActiveMessage] = useState(null);
  const [manualMessages, setManualMessages] = useState([]);
  const prevMessagesLength = useRef(0);

  // Combina mensagens do WS com mensagens manuais
  const messageMessages = useMemo(() => {
    // Em modo debug, só usa mensagens manuais
    if (debugMode) {
      return manualMessages;
    }

    // Em modo normal, combina WS + manuais
    const wsMessages = messages.filter(msg => msg.type === 'MESSAGE');
    return [...wsMessages, ...manualMessages];
  }, [messages, manualMessages, debugMode]);

  // Processa a fila de mensagens MESSAGE
  const processMessageQueue = (queue, currentTime) => {
    const DURATION = 3000; // 3 segundos por mensagem

    const foundMessage = queue.find(message => {
      const startTime = message.timestamp;
      const endTime = message.timestamp + DURATION;
      return currentTime >= startTime && currentTime < endTime;
    }) || null;

    return foundMessage || null;
  };

  // Atualiza mensagens ativas
  useEffect(() => {
    if (messageMessages.length === 0) return;

    if (messageMessages.length > prevMessagesLength.current) {
      prevMessagesLength.current = messageMessages.length;
    }
  }, [messageMessages]);

  // Intervalo para processar fila de mensagens
  useEffect(() => {
    const interval = setInterval(() => {
      const active = processMessageQueue(messageMessages, Date.now());
      setActiveMessage(active);
    }, 250);

    return () => clearInterval(interval);
  }, [messageMessages]);

  // Método para inserir mensagens manualmente
  const insertManualMessage = useCallback((content, duration = 3000, showTo = 'both') => {
    const manualMessage = {
      type: "MESSAGE",
      content,
      timestamp: Date.now(),
      id: `manual-${Date.now()}`,
      isManual: true,
      duration,
      showTo // Adiciona campo de direcionamento
    };

    setManualMessages(prev => [...prev, manualMessage]);

    // Remove após a duração especificada
    setTimeout(() => {
      setManualMessages(prev => prev.filter(msg => msg.id !== manualMessage.id));
    }, duration);
  }, []);

  return {
    messages: messageMessages,
    activeMessage,
    setActiveMessage,
    insertManualMessage
  };
}
